import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SiteRagReport } from '../../types/siteRag';
import { frozenDemonstrationBasisV1Schema } from '../../types/demonstration';
import { dataCenterFinancialModelV1Schema } from '../../types/dataCenterFinancial';
import type { DataCenterFinancialResult } from '../../types/dataCenterFinancial';
import {
  assertDataCenterFinancialSources,
  computeDataCenterFinance,
  type DataCenterFinancialSourceDocument,
} from '../dataCenterFinance';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';
import {
  buildReportSedMaterialDependencyManifest,
  reportSedMaterialDependencyDrift,
  verifyReportSedMaterialDependencyManifest,
} from '../decisionAnalysis/sedContentIdentity';

/** Producer-owned authority, independent of generated numbers or labels.
 * Source bodies are needed only when the saved model actually cites them. */
export interface FrozenModeledProjectBudgetSource {
  ownerUid: string;
  siteId: string;
  sed: SiteEvidenceDocument;
  documents?: readonly DataCenterFinancialSourceDocument[];
  expectedSedAuthority?: { id: string; version: number; snapshotHash?: string };
  expectedModelHash?: string;
}

export interface ModeledProjectBudgetEvidence {
  readonly ownerUid: string;
  readonly siteId: string;
  readonly sedId: string;
  readonly sedVersion: number;
  readonly modelHash: string;
  readonly modeledCapexUsd: number;
  readonly pricedBomUsd: number;
  readonly contingencyPct: number;
  readonly priceYear: number;
  readonly status: 'estimated';
  /** Recomputed by the same checked annual engine, not a published comparison. */
  readonly annualPayback: Readonly<Pick<DataCenterFinancialResult, 'paybackYears' | 'paybackMethod' | 'paybackClock'> & { referenceYear: number }>;
}

/** The annual engine owns this arithmetic. It never fills generation/PPA
 * scalar slots or makes a financial-scoring contribution outside that scope. */
export function buildModeledProjectBudgetEvidence(args: {
  source?: FrozenModeledProjectBudgetSource;
  report?: Pick<SiteRagReport, 'siteId' | 'sedId' | 'sedVersionUsed' | '_ownerUid'
    | 'reportType' | 'sedMaterialDependenciesAtGen'
    | 'demonstrationProvenance' | 'financialAssumptionsAtGen' | 'financialAnalysis'>;
}): ModeledProjectBudgetEvidence | undefined {
  const source = args.source;
  if (!source?.ownerUid || !source.siteId || !source.sed.id
    || !Number.isInteger(source.sed.version) || source.sed.version <= 0) return undefined;
  const expectedSed = source.expectedSedAuthority;
  if (expectedSed && (expectedSed.id !== source.sed.id || expectedSed.version !== source.sed.version
    || (expectedSed.snapshotHash !== undefined && expectedSed.snapshotHash !== hashCanonicalJson(source.sed)))) return undefined;
  const assumptions = source.sed.financialAssumptions;
  if (assumptions?.businessModelKind !== 'data-center-capacity-fee'
    || assumptions.siteId !== source.siteId || assumptions.currency !== 'USD') return undefined;
  const parsed = dataCenterFinancialModelV1Schema.safeParse(assumptions.dataCenterModel);
  if (!parsed.success) return undefined;
  const model = parsed.data;
  // An acknowledged unpriced scope cannot establish a complete project budget.
  if (model.ownerUid !== source.ownerUid || model.siteId !== source.siteId || model.unpricedScopes.length) return undefined;
  if (source.sed.demonstrationProvenance !== undefined) {
    const basis = frozenDemonstrationBasisV1Schema.safeParse(source.sed.demonstrationProvenance);
    if (!basis.success || basis.data.scope !== 'sed' || basis.data.ownerUid !== source.ownerUid
      || (model.authority === 'fictional-scenario' && basis.data.scenarioId !== model.scenarioId)) return undefined;
    if (model.sources.some(row => !basis.data.documents.some(entry => entry.documentId === row.documentId
      && entry.bodySha256 === row.bodySha256 && entry.originalFileSha256 === row.rawSha256
      && (model.authority !== 'fictional-scenario' || entry.sourceKind === 'fictional-scenario')))) return undefined;
  } else if (model.authority === 'fictional-scenario') return undefined;
  if (model.sources.some(row => source.documents?.filter(document => document.id === row.documentId).length !== 1)) return undefined;
  try {
    assertDataCenterFinancialSources(model, source.siteId, [...(source.documents ?? [])]);
  } catch { return undefined; }
  const result = computeDataCenterFinance(model);
  if (!result.computable || !result.modelHash || result.modeledCapexUsd == null
    || !Number.isFinite(result.modeledCapexUsd) || result.modeledCapexUsd < 0
    || model.pricedBomUsd == null || model.contingencyPct == null || model.priceYear == null
    || (source.expectedModelHash !== undefined && source.expectedModelHash !== result.modelHash)) return undefined;
  const report = args.report;
  if (report) {
    // The typed coordinator stamps optional outer metadata after its gate.
    // An existing contradiction is rejected, never normalized or overwritten.
    const sameSed = report.sedId === source.sed.id
      && (report.sedVersionUsed === undefined || report.sedVersionUsed === source.sed.version);
    // Only a separately admitted saved Financial report may quote its same
    // model after a nonmaterial SED rebuild. The actual current source and
    // declared prompt/full scope must reproduce its immutable dependency
    // receipt; model/source-body checks below remain independent and strict.
    let equivalentFinancialSed = false;
    const manifest = report.sedMaterialDependenciesAtGen;
    if (manifest !== undefined) {
      if (!report.reportType || !verifyReportSedMaterialDependencyManifest(manifest, report.reportType)) return undefined;
      try {
        const materialMatches = reportSedMaterialDependencyDrift(manifest,
          buildReportSedMaterialDependencyManifest(source.sed, report.reportType,
            { fullDocument: manifest.scope === 'full-document' })).current;
        if (!materialMatches) return undefined;
        equivalentFinancialSed = report.reportType === 'financial' && Boolean(report.sedId)
          && Number.isInteger(report.sedVersionUsed) && (report.sedVersionUsed ?? 0) > 0
          && report.siteId === source.siteId && report._ownerUid === source.ownerUid
          && Boolean(report.financialAssumptionsAtGen && report.financialAnalysis?.dataCenter);
      } catch { return undefined; }
    }
    if ((!sameSed && !equivalentFinancialSed)
      || (report.siteId !== undefined && report.siteId !== source.siteId)
      || (report._ownerUid !== undefined && report._ownerUid !== source.ownerUid)) return undefined;
    if (report.demonstrationProvenance !== undefined) {
      const reportBasis = frozenDemonstrationBasisV1Schema.safeParse(report.demonstrationProvenance);
      if (!reportBasis.success || reportBasis.data.scope !== 'report' || reportBasis.data.ownerUid !== source.ownerUid
        || !source.sed.demonstrationProvenance
        || reportBasis.data.scenarioId !== source.sed.demonstrationProvenance.scenarioId
        || (model.authority === 'fictional-scenario' && reportBasis.data.scenarioId !== model.scenarioId)) return undefined;
    }
    const frozenAssumptions = report.financialAssumptionsAtGen;
    if (frozenAssumptions && (frozenAssumptions.businessModelKind !== 'data-center-capacity-fee'
      || frozenAssumptions.siteId !== source.siteId || frozenAssumptions.currency !== 'USD'
      || computeDataCenterFinance(frozenAssumptions.dataCenterModel).modelHash !== result.modelHash)) return undefined;
    const frozen = report.financialAnalysis?.dataCenter;
    if (frozen && (report.financialAnalysis?.modelKind !== 'data-center-capacity-fee'
      || computeDataCenterFinance(frozen.model).modelHash !== result.modelHash
      || !frozen.result.computable || frozen.result.modelHash !== result.modelHash
      || frozen.result.modeledCapexUsd !== result.modeledCapexUsd)) return undefined;
  }
  return Object.freeze({ ownerUid: source.ownerUid, siteId: source.siteId,
    sedId: source.sed.id, sedVersion: source.sed.version, modelHash: result.modelHash,
    modeledCapexUsd: result.modeledCapexUsd, pricedBomUsd: model.pricedBomUsd,
    contingencyPct: model.contingencyPct, priceYear: model.priceYear, status: 'estimated',
    annualPayback: Object.freeze({ paybackYears: result.paybackYears, paybackMethod: result.paybackMethod,
      paybackClock: result.paybackClock, referenceYear: model.referenceYear! }) });
}

const MODELED = String.raw`model(?:ed|led)`;
const WHOLE = String.raw`(?:(?:total|whole[- ]project|project|campus|facility)\s+){0,3}`;
const COST = String.raw`(?:budget|CAPEX|capital\s+(?:budget|cost|expenditure))`;
const ROLE = String.raw`(?:${MODELED}\s+${WHOLE}${COST}|${WHOLE}(?:project\s+)?${MODELED}\s+${COST})`;
const LINK = String.raw`(?:(?:is|was|of|at|totals?|equals?|amounts?\s+to)\s+|[:=]\s*)?`;
const BEFORE_ROLE = new RegExp(String.raw`\b${ROLE}\s*${LINK}$`, 'i');
const AFTER_ROLE = new RegExp(String.raw`^\s+${ROLE}\b`, 'i');
const ATTEMPT_BEFORE = new RegExp(String.raw`\b${ROLE}([^$\n]{0,80})$`, 'i');
const ATTEMPT_AFTER = new RegExp(String.raw`^\s*(?:(?:is|was|not|as|a|an|the|quoted|contracted|awarded|paid)\s+){1,6}${ROLE}\b`, 'i');
const UPGRADE = String.raw`(?:quoted|contracted|awarded|paid|committed|verified|guaranteed|approved|certified|EPC|contract|quotation|quote|deposit|phase\s+(?:#\s*)?(?:[1-9]\d*|[ivx]+|one|two|three|four|five))`;
const SCOPED_BEFORE = new RegExp(String.raw`\b${UPGRADE}(?:\s+(?:contract|price|cost|amount|sum|value|budget|CAPEX|total|project|capital)){0,5}\s*${LINK}$`, 'i');
const SCOPED_AFTER = new RegExp(String.raw`^\s*(?:[,:(]\s*)?(?:(?:and|which|that)\s+)?(?:(?:is|was|has\s+been|will\s+be|represents?|for|of|as|equals?)\s+)?(?:(?:a|an|the|fully)\s+)?${UPGRADE}\b`, 'i');

/** Admit only this amount's explicitly modeled whole-project budget role.
 * Matching arithmetic is not proof of a quote, contract, phase or paid price.
 * Citation validity remains the separate ordinary citation audit's concern. */
export function checkModeledProjectBudgetNumber(args: {
  text: string; index: number; raw: string; value: number; unit: string; rangeLow?: number;
  evidence?: ModeledProjectBudgetEvidence;
}): { supported: boolean; expected: string } | undefined {
  if (args.unit !== 'USD') return undefined;
  const split = /[;\n]|\.(?=\s|$)|\b(?:but|whereas|however)\b/i;
  const before = args.text.slice(Math.max(0, args.index - 400), args.index);
  const after = args.text.slice(args.index + args.raw.length, args.index + args.raw.length + 400);
  const lead = before.split(split).pop() ?? '';
  const tail = after.split(split)[0] ?? '';
  const roleBefore = BEFORE_ROLE.exec(lead);
  const roleAfter = AFTER_ROLE.exec(tail);
  const attemptBefore = ATTEMPT_BEFORE.exec(lead);
  const attempted = (attemptBefore && !/\bUSD\b/i.test(attemptBefore[1]!)) || ATTEMPT_AFTER.test(tail);
  if (!roleBefore && !roleAfter && !attempted) return undefined;
  const expected = args.evidence
    ? `${args.evidence.modeledCapexUsd} USD estimated whole-project budget from saved annual model ${args.evidence.modelHash}`
    : 'A complete schema-valid, computable frozen same-owner/site/SED annual data-center model with no unpriced scope.';
  const failed = { supported: false, expected };
  if (!roleBefore && !roleAfter) return failed;
  const prefix = roleBefore ? lead.slice(0, roleBefore.index) : lead;
  const suffix = roleAfter ? tail.slice(roleAfter[0].length) : tail;
  if (SCOPED_BEFORE.test(prefix) || SCOPED_AFTER.test(suffix)
    || /\b(?:not|never|no)\s*$|[+\-×*/<>]\s*$|\b(?:at\s+least|at\s+most|more\s+than|less\s+than)\s*$/i.test(lead)
    || /\b(?:not|never|no)\s+(?:(?:a|an|the|this)\s+)?$|\b(?:phase\s+[1-9]\d*|component|equipment|diesel|fuel|UPS|transformer|generator)\s*$/i.test(prefix)
    || /^\s*(?:[+\-×*/<>]|(?:x|times|multiplied\s+by|divided\s+by)\b)/i.test(suffix)
    || args.rangeLow !== undefined || !Number.isFinite(args.value) || args.value < 0) return failed;
  const priceYear = /\b(?:constant\s+)?(\d{4})\s+(?:USD|dollars)\b/i.exec(`${lead}${args.raw}${tail}`);
  if (priceYear && Number(priceYear[1]) !== args.evidence?.priceYear) return failed;
  const value = args.evidence?.modeledCapexUsd;
  // Same 1% USD tolerance as groundingValidator: rounded money and IEEE
  // arithmetic remain valid, while another project/case budget cannot borrow it.
  return { supported: value !== undefined && Math.abs(args.value - value) / Math.max(Math.abs(args.value), Math.abs(value), 1) <= 0.01, expected };
}

/** Producer context exposes only the admitted role, not a legacy FA scalar. */
export function modeledProjectBudgetPromptBlock(evidence?: ModeledProjectBudgetEvidence): string {
  if (!evidence) return '';
  return `ESTIMATED MODELED WHOLE-PROJECT BUDGET — saved annual model ${evidence.modelHash}\n`
    + `The modeled whole-project budget is $${evidence.modeledCapexUsd.toLocaleString('en-US', { maximumFractionDigits: 2 })} in constant ${evidence.priceYear} USD.\n`
    + `Its priced BOM subtotal is ${evidence.pricedBomUsd} USD plus ${evidence.contingencyPct}% contingency applied once.\n`
    + 'Keep the priced BOM subtotal separate from the modeled whole-project budget; do not rename the subtotal as total/base CAPEX or an awarded price. Keep customer allocated service separate from selected phase facility demand and hardware nameplate capacity.\n'
    + 'Payback and capital intensity require a separately admitted annual financial-metrics context; if it is absent, omit those model-derived numbers rather than compute them from this budget or a source comparison.\n'
    + 'This planning-model budget does not establish an OEM quote, contract/EPC/award/paid price, phase budget or generation/PPA financial result. Cite the relevant source separately where required.\n';
}
